import {
  type Command,
  PROTOCOL_VERSION,
  type ServerEnvelope,
  type ServerEvent,
  serverEnvelopeSchema,
} from '@sea-wars/protocol';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

interface Pending {
  readonly id: string;
  readonly cmd: Command;
}

export interface ConnectionHandlers {
  onEvent(evt: ServerEvent, seq: number): void;
  onStatus(status: ConnectionStatus): void;
  onError(code: string, message: string): void;
}

const BACKOFF_MS = [500, 1000, 2000, 4000, 8000];

/**
 * Transport rozgrywki.
 *
 * Trzy mechanizmy ratują partię na zawodnej sieci mobilnej i wszystkie trzy wymagają
 * wsparcia po stronie serwera, które już istnieje:
 *
 *  1. Każda komenda ma identyfikator i czeka w kolejce do czasu potwierdzenia.
 *     Po ponownym połączeniu wysyłamy ją jeszcze raz z TYM SAMYM identyfikatorem —
 *     serwer rozpozna powtórkę i nie wystrzeli drugi raz.
 *  2. Pamiętamy ostatni numer `seq`, więc po powrocie dostajemy samą różnicę,
 *     a nie pełny stan.
 *  3. Powrót karty z tła natychmiast wymusza próbę połączenia, zamiast czekać
 *     na timeout TCP, który potrafi trwać dziesiątki sekund.
 */
export class GameConnection {
  private socket: WebSocket | null = null;
  private readonly pending = new Map<string, Pending>();
  private attempt = 0;
  private lastSeq = 0;
  private gameId: string | null = null;
  private closedByUser = false;
  private reconnectTimer: number | null = null;

  constructor(private readonly handlers: ConnectionHandlers) {}

  /** Otwiera połączenie i podłącza się do partii o danym identyfikatorze. */
  open(gameId: string): void {
    this.gameId = gameId;
    this.closedByUser = false;
    this.connect();

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('online', this.onBackOnline);
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') this.reviveNow();
  };

  private readonly onBackOnline = (): void => this.reviveNow();

  /** Natychmiastowa próba połączenia z pominięciem odczekania. */
  private reviveNow(): void {
    if (this.closedByUser) return;
    if (this.socket && this.socket.readyState === WebSocket.OPEN) return;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempt = 0;
    this.connect();
  }

  private connect(): void {
    if (this.closedByUser) return;
    this.handlers.onStatus(this.lastSeq > 0 ? 'reconnecting' : 'connecting');

    // `reviveNow()` może wywołać `connect()`, gdy poprzednie gniazdo wciąż jest
    // `CONNECTING` (jego `readyState` to jeszcze nie `OPEN`, więc guard w `reviveNow`
    // go nie złapie). Bez odpięcia uchwytów spóźniony `onopen` starego gniazda odpaliłby
    // się i wysłał przez `this.socket`, który w międzyczasie wskazuje już na NOWE,
    // wciąż łączące się gniazdo — stąd `InvalidStateError: Still in CONNECTING state`
    // widoczne w Sentry (SEA-WARS-WEB-3).
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
    }

    const url = new URL('/ws', window.location.href);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const socket = new WebSocket(url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.handlers.onStatus('open');
      // Wejście do partii i każde wznowienie — zawsze przez resume (od `lastSeq` po zerwaniu).
      if (this.gameId) {
        this.rawSend(crypto.randomUUID(), {
          type: 'session.resume',
          gameId: this.gameId,
          lastSeq: this.lastSeq,
        });
      }
      // Powtórka komend, które nie zdążyły dostać potwierdzenia.
      for (const item of this.pending.values()) this.rawSend(item.id, item.cmd);
    };

    socket.onmessage = (message) => this.receive(message.data);

    socket.onclose = () => {
      if (this.closedByUser) return;
      this.handlers.onStatus('reconnecting');
      const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)] ?? 8000;
      this.attempt += 1;
      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    };

    socket.onerror = () => socket.close();
  }

  private receive(raw: unknown): void {
    if (typeof raw !== 'string') return;
    let parsed: ServerEnvelope;
    try {
      parsed = serverEnvelopeSchema.parse(JSON.parse(raw));
    } catch {
      this.handlers.onError('BAD_REQUEST', 'Serwer przysłał ramkę, której nie rozumiem');
      return;
    }

    if (parsed.kind === 'ack') {
      this.pending.delete(parsed.id);
      return;
    }
    if (parsed.kind === 'err') {
      if (parsed.id) this.pending.delete(parsed.id);
      this.handlers.onError(parsed.code, parsed.message);
      return;
    }

    // Snapshot nie posuwa numeracji — ustawia punkt odniesienia.
    this.lastSeq = Math.max(this.lastSeq, parsed.seq);
    if (parsed.evt.type === 'game.snapshot') this.gameId = parsed.evt.view.gameId;
    this.handlers.onEvent(parsed.evt, parsed.seq);
  }

  private rawSend(id: string, cmd: Command): void {
    this.socket?.send(JSON.stringify({ v: PROTOCOL_VERSION, kind: 'cmd', id, cmd }));
  }

  /** Wysyła komendę; przy braku połączenia czeka w kolejce do najbliższego otwarcia. */
  send(cmd: Command): void {
    const id = crypto.randomUUID();
    this.pending.set(id, { id, cmd });
    if (this.socket?.readyState === WebSocket.OPEN) this.rawSend(id, cmd);
  }

  close(): void {
    this.closedByUser = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onBackOnline);
    this.socket?.close();
    this.socket = null;
    this.handlers.onStatus('closed');
  }
}
